/**
 * Reportes — diseño "Reportes.dc" (Claude Design).
 *
 * Header (ModuleShell) + pestañas (Ventas por cliente / Compras por proveedor /
 * Devoluciones / Inventario). Cada pestaña: fila de 3 KPIs con barra de color,
 * y debajo gráficos (ranking de barras + donut SVG) y tablas/listas. Todo con
 * datos reales de los endpoints /reports/* (filtrables por periodo).
 */
import { useMemo, useState } from 'react'
import { BarChart3 } from 'lucide-react'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { Segmented } from '@/components/ui/Segmented'
import { FilterBar, DateMenu } from '@/components/ui/FilterBar'
import { ScrollX } from '@/components/ui/ScrollX'
import { useApi } from '@/lib/useApi'
import { fmtMoney } from '@/lib/utils'

const fmtQty = (n) => Number(n || 0).toLocaleString('es-CO')
const money = (n) => `USD ${fmtMoney(n)}`
const PALETTE = ['#1E6E3A', '#6FB07A', '#A9C93C', '#C3DB5A', '#5DA567', '#8FAE6B', '#3E7A4A']
const initialsOf = (name = '') =>
  name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '—'

const REPORT_TABS = [
  { id: 'sales', label: 'Ventas' },
  { id: 'purchases', label: 'Compras' },
  { id: 'returns', label: 'Devoluciones' },
  { id: 'inventory', label: 'Inventario' },
]

// ── Presentacionales ───────────────────────────────────────────────────
function Panel({ title, sub, right, children, className }) {
  return (
    <div
      className={className}
      style={{ background: '#fff', border: '1px solid #E6EFE3', borderRadius: 22, padding: '26px 28px', boxShadow: '0 6px 24px rgba(20,80,42,0.05)' }}
    >
      {(title || right) && (
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            {title && (
              <div className="font-display" style={{ fontSize: 24, fontWeight: 800, color: '#143A22', lineHeight: 1.1 }}>
                {title}
              </div>
            )}
            {sub && <div style={{ fontSize: 14, color: '#8B9B8F', fontWeight: 500, marginTop: 4 }}>{sub}</div>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  )
}

function KpiRow({ kpis }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px]">
      {kpis.map((k, i) => (
        <div key={i} style={{ position: 'relative', overflow: 'hidden', background: '#fff', border: '1px solid #E6EFE3', borderRadius: 20, padding: '22px 24px' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, background: k.color }} />
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', color: '#9BA89B', textTransform: 'uppercase' }}>{k.label}</div>
          <div className="font-display" style={{ fontSize: 34, fontWeight: 800, color: '#143A22', lineHeight: 1, marginTop: 13 }}>{k.value}</div>
          <div style={{ fontSize: 13, color: '#7E8A80', fontWeight: 600, marginTop: 9 }}>{k.sub}</div>
        </div>
      ))}
    </div>
  )
}

function RankingList({ rows }) {
  if (rows.length === 0) {
    return <div style={{ fontSize: 14, color: '#8B9B8F', padding: '18px 0' }}>Sin datos en el periodo.</div>
  }
  return (
    <div style={{ marginTop: 6 }}>
      {rows.map((c, i) => (
        <div key={i} className="flex items-center" style={{ gap: 18, padding: '18px 0', borderTop: '1px solid #EEF2EC' }}>
          <div className="flex items-center justify-center flex-none" style={{ width: 46, height: 46, borderRadius: 13, background: '#033021', color: '#C3DB5A', fontSize: 15, fontWeight: 800 }}>
            {c.code}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="flex items-baseline justify-between" style={{ gap: 12, marginBottom: 10 }}>
              <span className="truncate" style={{ fontSize: 18, fontWeight: 700, color: '#143A22' }}>{c.name}</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: '#143A22', whiteSpace: 'nowrap' }}>{c.valueRight}</span>
            </div>
            <div className="flex items-center" style={{ gap: 14 }}>
              <div style={{ flex: 1, height: 12, borderRadius: 999, background: '#EEF2EC', overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 999, background: c.color, width: c.barPct }} />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#7E8A80', whiteSpace: 'nowrap' }}>{c.subRight}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function Donut({ centerValue, centerLabel, segments }) {
  const r = 56
  const circ = 2 * Math.PI * r
  const shown = segments.filter((s) => s.value > 0)
  const sum = shown.reduce((a, s) => a + s.value, 0) || 1
  let cum = 0
  const arcs = shown.map((s) => {
    const len = (s.value / sum) * circ
    const arc = { color: s.color, dash: `${Math.max(0, len - 2).toFixed(2)} ${circ.toFixed(2)}`, offset: (-cum).toFixed(2) }
    cum += len
    return arc
  })
  return (
    <>
      <div className="flex justify-center" style={{ margin: '18px 0 20px' }}>
        <div style={{ position: 'relative', width: 172, height: 172 }}>
          <svg width="172" height="172" viewBox="0 0 140 140" style={{ display: 'block' }}>
            <circle cx="70" cy="70" r="56" fill="none" stroke="#EEF2EC" strokeWidth="18" />
            {arcs.map((a, i) => (
              <circle key={i} cx="70" cy="70" r="56" fill="none" stroke={a.color} strokeWidth="18" strokeDasharray={a.dash} strokeDashoffset={a.offset} transform="rotate(-90 70 70)" />
            ))}
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <span className="font-display" style={{ fontSize: 25, fontWeight: 800, color: '#143A22', lineHeight: 1 }}>{centerValue}</span>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: '#9BA89B', marginTop: 4 }}>{centerLabel}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col" style={{ gap: 14 }}>
        {shown.length === 0 && <span style={{ fontSize: 14, color: '#8B9B8F' }}>Sin datos.</span>}
        {shown.map((s, i) => (
          <div key={i} className="flex items-center" style={{ gap: 11 }}>
            <span style={{ width: 12, height: 12, borderRadius: 4, background: s.color, flex: 'none' }} />
            <span className="truncate" style={{ fontSize: 15, fontWeight: 600, color: '#5C6B60', flex: 1, minWidth: 0 }}>{s.label}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#9BA89B', whiteSpace: 'nowrap' }}>{s.disp}</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#143A22', width: 44, textAlign: 'right' }}>
              {Math.round((s.value / sum) * 100)}%
            </span>
          </div>
        ))}
      </div>
    </>
  )
}

const TH_STYLE = { fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9BA89B' }

// ── Página ─────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const [tab, setTab] = useState('sales')
  const today = new Date().toISOString().slice(0, 10)
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  const [from, setFrom] = useState(monthAgo)
  const [to, setTo] = useState(today)
  const [snapshotAt, setSnapshotAt] = useState('')

  const qs = useMemo(() => {
    const p = new URLSearchParams()
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    return p.toString() ? `?${p.toString()}` : ''
  }, [from, to])

  const sales = useApi(tab === 'sales' ? `/reports/sales${qs}` : null, { enabled: tab === 'sales' })
  const purchases = useApi(tab === 'purchases' ? `/reports/purchases${qs}` : null, { enabled: tab === 'purchases' })
  const returns = useApi(tab === 'returns' ? `/reports/returns${qs}` : null, { enabled: tab === 'returns' })
  const inventory = useApi(
    tab === 'inventory' ? `/reports/inventory${snapshotAt ? `?at=${snapshotAt}` : ''}` : null,
    { enabled: tab === 'inventory' },
  )

  // ── Ventas ──
  const ventas = useMemo(() => {
    const d = sales.data
    if (!d) return null
    const rows = Object.entries(d.byClient || {})
      .map(([id, o]) => ({ id, name: o.name || id, usd: Number(o.usd) || 0, stems: Number(o.stems) || 0 }))
      .sort((a, b) => b.usd - a.usd)
    const maxUsd = Math.max(1, ...rows.map((r) => r.usd))
    const totalUsd = rows.reduce((a, r) => a + r.usd, 0)
    const totalStems = rows.reduce((a, r) => a + r.stems, 0)
    return { rows, maxUsd, totalUsd, totalStems, count: d.count || 0 }
  }, [sales.data])

  // ── Compras ──
  const compras = useMemo(() => {
    const d = purchases.data
    if (!d) return null
    const rows = Object.entries(d.bySupplier || {})
      .map(([k, o]) => ({ id: k, name: o.name || k, stems: Number(o.stems) || 0, usd: Number(o.usd) || 0, cop: Number(o.cop) || 0 }))
      .sort((a, b) => b.stems - a.stems)
    const maxStems = Math.max(1, ...rows.map((r) => r.stems))
    const totalStems = rows.reduce((a, r) => a + r.stems, 0)
    const totalUsd = rows.reduce((a, r) => a + r.usd, 0)
    const totalCop = rows.reduce((a, r) => a + r.cop, 0)
    return { rows, maxStems, totalStems, totalUsd, totalCop, count: d.count || 0 }
  }, [purchases.data])

  // ── Devoluciones ──
  const devol = useMemo(() => {
    const d = returns.data
    if (!d) return null
    const compra = Object.entries(d.bySupplier || {}).map(([k, o]) => ({ name: k, stems: Number(o.stems) || 0, count: Number(o.count) || 0 }))
    const venta = Object.entries(d.byClient || {}).map(([k, o]) => ({ name: k, stems: Number(o.stems) || 0, count: Number(o.count) || 0 }))
    const compraStems = compra.reduce((a, r) => a + r.stems, 0)
    const ventaStems = venta.reduce((a, r) => a + r.stems, 0)
    const max = Math.max(1, ...compra.map((r) => r.stems), ...venta.map((r) => r.stems))
    return { compra, venta, compraStems, ventaStems, max, count: d.count || 0 }
  }, [returns.data])

  // ── Inventario ──
  const inv = useMemo(() => {
    const d = inventory.data
    if (!d) return null
    const items = (d.items || [])
      .map((r) => {
        const sin = Number(r.stages?.unprocessed) || 0
        const proc = Number(r.stages?.cold_room) || 0
        return { variety: r.variety, len: `${r.stemLength}cm`, sin, proc, total: sin + proc }
      })
      .filter((r) => r.total > 0)
      .sort((a, b) => b.total - a.total)
    const totalSin = items.reduce((a, r) => a + r.sin, 0)
    const totalProc = items.reduce((a, r) => a + r.proc, 0)
    const maxRow = Math.max(1, ...items.map((r) => r.total))
    const byVarMap = new Map()
    for (const r of items) byVarMap.set(r.variety, (byVarMap.get(r.variety) || 0) + r.total)
    const byVariety = Array.from(byVarMap.entries())
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6)
    const maxVar = Math.max(1, ...byVariety.map((v) => v.total))
    return { items, totalSin, totalProc, maxRow, byVariety, maxVar, total: totalSin + totalProc }
  }, [inventory.data])

  // ── KPIs por pestaña ──
  const kpis = useMemo(() => {
    if (tab === 'sales' && ventas) {
      return [
        { label: 'Ingresos totales', value: money(ventas.totalUsd), sub: 'Suma del periodo', color: '#1E6E3A' },
        { label: 'Tallos vendidos', value: fmtQty(ventas.totalStems), sub: `En ${ventas.count} venta${ventas.count === 1 ? '' : 's'}`, color: '#6FB07A' },
        { label: 'Clientes activos', value: ventas.rows.length, sub: 'Con ventas registradas', color: '#A9C93C' },
      ]
    }
    if (tab === 'purchases' && compras) {
      return [
        { label: 'Tallos comprados', value: fmtQty(compras.totalStems), sub: `De ${compras.rows.length} proveedor${compras.rows.length === 1 ? '' : 'es'}`, color: '#1E6E3A' },
        { label: 'Proveedores', value: compras.rows.length, sub: 'Con compras registradas', color: '#6FB07A' },
        { label: 'Valor registrado', value: money(compras.totalUsd), sub: compras.totalCop ? `+ COP ${fmtQty(compras.totalCop)}` : 'USD en el periodo', color: '#A9C93C' },
      ]
    }
    if (tab === 'returns' && devol) {
      return [
        { label: 'Tallos devueltos', value: fmtQty(devol.compraStems + devol.ventaStems), sub: 'Compra + venta', color: '#1E6E3A' },
        { label: 'Devoluciones', value: devol.count, sub: `${devol.compra.length} de compra · ${devol.venta.length} de venta`, color: '#6FB07A' },
        { label: 'Devueltos por venta', value: fmtQty(devol.ventaStems), sub: `De ${devol.venta.length} cliente${devol.venta.length === 1 ? '' : 's'}`, color: '#A9C93C' },
      ]
    }
    if (tab === 'inventory' && inv) {
      return [
        { label: 'Sin procesar', value: fmtQty(inv.totalSin), sub: 'Listo para proceso', color: '#A9C93C' },
        { label: 'Procesado', value: fmtQty(inv.totalProc), sub: 'En cuarto frío', color: '#1E6E3A' },
        { label: 'Total en bodega', value: fmtQty(inv.total), sub: 'Tallos disponibles', color: '#6FB07A' },
      ]
    }
    return null
  }, [tab, ventas, compras, devol, inv])

  const loading =
    (tab === 'sales' && sales.loading) ||
    (tab === 'purchases' && purchases.loading) ||
    (tab === 'returns' && returns.loading) ||
    (tab === 'inventory' && inventory.loading)

  return (
    <ModuleShell
      title="Reportes"
      subtitle="Ventas, compras, devoluciones e inventario"
      icon={BarChart3}
      filters={
        <FilterBar
          left={<Segmented options={REPORT_TABS.map((t) => ({ value: t.id, label: t.label }))} value={tab} onChange={setTab} />}
          right={
            <DateMenu>
              {tab !== 'inventory' ? (
                <div className="flex flex-col gap-3" style={{ minWidth: 220 }}>
                  <label className="text-sm" style={{ color: '#5C6B60', fontWeight: 600 }}>
                    Desde
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={{ border: '1px solid #E6EFE3', outline: 'none' }} />
                  </label>
                  <label className="text-sm" style={{ color: '#5C6B60', fontWeight: 600 }}>
                    Hasta
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={{ border: '1px solid #E6EFE3', outline: 'none' }} />
                  </label>
                </div>
              ) : (
                <label className="text-sm" style={{ color: '#5C6B60', fontWeight: 600, display: 'block', minWidth: 220 }}>
                  Snapshot al (vacío = ahora)
                  <input type="date" value={snapshotAt} onChange={(e) => setSnapshotAt(e.target.value)} className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={{ border: '1px solid #E6EFE3', outline: 'none' }} />
                </label>
              )}
            </DateMenu>
          }
        />
      }
    >
      <div className="flex flex-col" style={{ gap: 22 }}>
        {loading && <p style={{ color: '#8B9B8F' }}>Cargando…</p>}

        {/* KPIs (compartido) */}
        {kpis && <KpiRow kpis={kpis} />}

        {/* ── VENTAS POR CLIENTE ── */}
        {tab === 'sales' && ventas && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-[1.45fr_1fr] gap-[22px] items-start">
              <Panel title="Ingresos por cliente" sub="Ranking por valor del periodo (USD)">
                <RankingList
                  rows={ventas.rows.map((c, i) => ({
                    code: initialsOf(c.name),
                    name: c.name,
                    valueRight: money(c.usd),
                    barPct: `${(c.usd / ventas.maxUsd) * 100}%`,
                    color: PALETTE[i % PALETTE.length],
                    subRight: `${fmtQty(c.stems)} tallos`,
                  }))}
                />
              </Panel>
              <Panel title="Participación" sub="Reparto de ingresos por cliente">
                <Donut
                  centerValue={fmtQty(Math.round(ventas.totalUsd))}
                  centerLabel="USD TOTAL"
                  segments={ventas.rows.map((c, i) => ({ label: c.name, value: c.usd, color: PALETTE[i % PALETTE.length], disp: money(c.usd) }))}
                />
              </Panel>
            </div>
            <Panel>
              <div className="font-display" style={{ fontSize: 20, fontWeight: 800, color: '#143A22', marginBottom: 6 }}>Detalle por cliente</div>
              <ScrollX fadeColor="#fff">
              <div style={{ minWidth: 460 }}>
              <div className="grid" style={{ gridTemplateColumns: '2fr 1fr 1fr', gap: 16, padding: '8px 0', ...TH_STYLE }}>
                <span>Cliente</span>
                <span style={{ textAlign: 'right' }}>Tallos</span>
                <span style={{ textAlign: 'right' }}>Valor (USD)</span>
              </div>
              {ventas.rows.map((r) => (
                <div key={r.id} className="grid items-center" style={{ gridTemplateColumns: '2fr 1fr 1fr', gap: 16, padding: '16px 0', borderTop: '1px solid #EEF2EC' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#143A22' }}>{r.name}</span>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#3F4F44', textAlign: 'right' }}>{fmtQty(r.stems)}</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#1E6E3A', textAlign: 'right' }}>{fmtMoney(r.usd)}</span>
                </div>
              ))}
              </div>
              </ScrollX>
            </Panel>
          </>
        )}

        {/* ── COMPRAS POR PROVEEDOR ── */}
        {tab === 'purchases' && compras && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-[1.45fr_1fr] gap-[22px] items-start">
              <Panel title="Compras por proveedor" sub="Volumen comprado en el periodo (tallos)">
                <RankingList
                  rows={compras.rows.map((c, i) => ({
                    code: initialsOf(c.name),
                    name: c.name,
                    valueRight: `${fmtQty(c.stems)} tallos`,
                    barPct: `${(c.stems / compras.maxStems) * 100}%`,
                    color: PALETTE[i % PALETTE.length],
                    subRight: c.usd ? money(c.usd) : c.cop ? `COP ${fmtQty(c.cop)}` : 'USD 0.00',
                  }))}
                />
              </Panel>
              <Panel title="Participación" sub="Reparto de compras por proveedor">
                <Donut
                  centerValue={fmtQty(compras.totalStems)}
                  centerLabel="TALLOS"
                  segments={compras.rows.map((c, i) => ({ label: c.name, value: c.stems, color: PALETTE[i % PALETTE.length], disp: `${fmtQty(c.stems)} tallos` }))}
                />
              </Panel>
            </div>
            <Panel>
              <div className="font-display" style={{ fontSize: 20, fontWeight: 800, color: '#143A22', marginBottom: 6 }}>Detalle por proveedor</div>
              <ScrollX fadeColor="#fff">
              <div style={{ minWidth: 560 }}>
              <div className="grid" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 16, padding: '8px 0', ...TH_STYLE }}>
                <span>Proveedor</span>
                <span style={{ textAlign: 'right' }}>Tallos</span>
                <span style={{ textAlign: 'right' }}>Valor USD</span>
                <span style={{ textAlign: 'right' }}>Valor COP</span>
              </div>
              {compras.rows.map((r) => (
                <div key={r.id} className="grid items-center" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 16, padding: '16px 0', borderTop: '1px solid #EEF2EC' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#143A22' }}>{r.name}</span>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#3F4F44', textAlign: 'right' }}>{fmtQty(r.stems)}</span>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#9BA89B', textAlign: 'right' }}>{fmtMoney(r.usd)}</span>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#9BA89B', textAlign: 'right' }}>{fmtQty(r.cop)}</span>
                </div>
              ))}
              </div>
              </ScrollX>
            </Panel>
          </>
        )}

        {/* ── DEVOLUCIONES ── */}
        {tab === 'returns' && devol && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.45fr] gap-[22px] items-start">
            <Panel title="Origen" sub="Tallos devueltos por tipo">
              <Donut
                centerValue={fmtQty(devol.compraStems + devol.ventaStems)}
                centerLabel="TALLOS"
                segments={[
                  { label: 'De venta', value: devol.ventaStems, color: '#1E6E3A', disp: `${fmtQty(devol.ventaStems)} tallos` },
                  { label: 'De compra', value: devol.compraStems, color: '#6FB07A', disp: `${fmtQty(devol.compraStems)} tallos` },
                ]}
              />
            </Panel>
            <div className="flex flex-col" style={{ gap: 22 }}>
              <Panel title="Devoluciones de compra" right={<span style={{ fontSize: 13, fontWeight: 700, color: '#9BA89B' }}>por proveedor</span>}>
                <RankingList
                  rows={devol.compra.map((d, i) => ({
                    code: initialsOf(d.name),
                    name: d.name,
                    valueRight: `${fmtQty(d.stems)} tallos`,
                    barPct: `${(d.stems / devol.max) * 100}%`,
                    color: PALETTE[i % PALETTE.length],
                    subRight: `${d.count} devol.`,
                  }))}
                />
              </Panel>
              <Panel title="Devoluciones de venta" right={<span style={{ fontSize: 13, fontWeight: 700, color: '#9BA89B' }}>por cliente</span>}>
                <RankingList
                  rows={devol.venta.map((d, i) => ({
                    code: initialsOf(d.name),
                    name: d.name,
                    valueRight: `${fmtQty(d.stems)} tallos`,
                    barPct: `${(d.stems / devol.max) * 100}%`,
                    color: PALETTE[i % PALETTE.length],
                    subRight: `${d.count} devol.`,
                  }))}
                />
              </Panel>
            </div>
          </div>
        )}

        {/* ── INVENTARIO ── */}
        {tab === 'inventory' && inv && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.45fr] gap-[22px] items-start">
              <Panel title="Estado del stock" sub="Sin procesar vs. procesado">
                <Donut
                  centerValue={fmtQty(inv.total)}
                  centerLabel="TALLOS"
                  segments={[
                    { label: 'Sin procesar', value: inv.totalSin, color: '#C3DB5A', disp: `${fmtQty(inv.totalSin)} tallos` },
                    { label: 'Procesado', value: inv.totalProc, color: '#1E6E3A', disp: `${fmtQty(inv.totalProc)} tallos` },
                  ]}
                />
              </Panel>
              <Panel title="Stock por variedad" sub="Tallos totales agrupados por variedad">
                <div style={{ marginTop: 8 }}>
                  {inv.byVariety.length === 0 && <div style={{ fontSize: 14, color: '#8B9B8F', padding: '12px 0' }}>Sin stock.</div>}
                  {inv.byVariety.map((v, i) => (
                    <div key={v.name} className="flex items-center" style={{ gap: 16, padding: '15px 0', borderTop: '1px solid #EEF2EC' }}>
                      <span className="truncate" style={{ width: 128, flex: 'none', fontSize: 16, fontWeight: 700, color: '#143A22' }}>{v.name}</span>
                      <div style={{ flex: 1, height: 13, borderRadius: 999, background: '#EEF2EC', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 999, background: PALETTE[i % PALETTE.length], width: `${(v.total / inv.maxVar) * 100}%` }} />
                      </div>
                      <span style={{ width: 56, flex: 'none', textAlign: 'right', fontSize: 16, fontWeight: 800, color: '#143A22' }}>{fmtQty(v.total)}</span>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>

            <Panel>
              <div className="flex items-baseline justify-between gap-4 flex-wrap" style={{ marginBottom: 8 }}>
                <div>
                  <div className="font-display" style={{ fontSize: 24, fontWeight: 800, color: '#143A22', lineHeight: 1.1 }}>Inventario por variedad y longitud</div>
                  <div style={{ fontSize: 14, color: '#8B9B8F', fontWeight: 500, marginTop: 4 }}>Desglose del procesamiento de cada lote</div>
                </div>
                <div className="flex" style={{ gap: 20 }}>
                  <span className="flex items-center" style={{ gap: 8, fontSize: 14, fontWeight: 600, color: '#5C6B60' }}><span style={{ width: 12, height: 12, borderRadius: 4, background: '#C3DB5A' }} />Sin procesar</span>
                  <span className="flex items-center" style={{ gap: 8, fontSize: 14, fontWeight: 600, color: '#5C6B60' }}><span style={{ width: 12, height: 12, borderRadius: 4, background: '#1E6E3A' }} />Procesado</span>
                </div>
              </div>
              <ScrollX fadeColor="#fff">
              <div style={{ minWidth: 620 }}>
              {inv.items.map((r, i) => (
                <div key={i} className="flex items-center" style={{ gap: 18, padding: '16px 0', borderTop: '1px solid #EEF2EC' }}>
                  <div className="flex items-center justify-center flex-none" style={{ width: 40, height: 40, borderRadius: 12, background: '#033021', color: '#C3DB5A', fontSize: 15, fontWeight: 800 }}>
                    {String(r.variety).charAt(0).toUpperCase()}
                  </div>
                  <div style={{ width: 150, flex: 'none' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#143A22', lineHeight: 1.1 }}>{r.variety}</div>
                    <div style={{ fontSize: 13, color: '#8B9B8F', fontWeight: 600, marginTop: 2 }}>{r.len}</div>
                  </div>
                  <div className="flex" style={{ flex: 1, height: 14, borderRadius: 999, background: '#F2F5F0', overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#C3DB5A', width: `${(r.sin / inv.maxRow) * 100}%` }} />
                    <div style={{ height: '100%', background: '#1E6E3A', width: `${(r.proc / inv.maxRow) * 100}%` }} />
                  </div>
                  <div className="flex justify-end items-baseline" style={{ width: 210, flex: 'none', gap: 18 }}>
                    <span style={{ fontSize: 14, color: '#7E8A80', fontWeight: 600 }}>SP <b style={{ color: '#143A22', fontWeight: 800 }}>{fmtQty(r.sin)}</b></span>
                    <span style={{ fontSize: 14, color: '#7E8A80', fontWeight: 600 }}>P <b style={{ color: '#143A22', fontWeight: 800 }}>{fmtQty(r.proc)}</b></span>
                    <span style={{ fontSize: 17, color: '#1E6E3A', fontWeight: 800, minWidth: 44, textAlign: 'right' }}>{fmtQty(r.total)}</span>
                  </div>
                </div>
              ))}
              </div>
              </ScrollX>
            </Panel>
          </>
        )}
      </div>
    </ModuleShell>
  )
}
